import { useIntl } from 'react-intl'
import { ActionsProps } from '@/app/models/action'
import ActionCard from '@/app/components/UI/ActionCard/ActionCard'
import ActivitySummaryChart from '../../UI/ActivitySummaryChart/ActivitySummaryChart'
import { useEffect, useState } from 'react'
import {
    LuxButton,
    LuxDataTable,
    LuxBadgeLabel,
    LuxRichInputText,
    LUX_INPUT_TYPE,
    LuxIcon,
    LUX_TEMPLATE_TYPE,
} from '@lux/wardrobe-lib'
import { connect } from 'react-redux'
import { setIsMultiStore } from '@/app/redux/actions/activity-execution'
import { statusColors } from '@/app/utils/statusColorMap'
import { FilterMatchMode } from 'primereact/api'
import SectionItem from '../../Layout/SectionItem/SectionItem'
import AddAttachment from '../../UI/AddAttachment/AddAttachment'
import { ProjectsService } from '@/app/services/ProjectsService'
import {
    canExecuteQuestionTask,
    LayoutActivityType,
    SmartlyActionSubtypes,
} from '@Techedge/digitalportal-shared'
import Photo from './Photo/Photo'
import './Action.scss'
import { SectionFilter } from '@/app/models/search'

export const columns = [
    {
        field: 'storeName',
        header: 'modals.new.activity.section.distribution.table.store.name',
        filterType: LUX_INPUT_TYPE.text,
    },
    {
        field: 'storeId',
        header: 'modals.new.activity.section.distribution.table.store.id',
        filterType: LUX_INPUT_TYPE.text,
    },
    {
        field: 'status',
        header: 'modals.new.activity.section.distribution.table.status',
        template: (rowData) => {
            return (
                <LuxBadgeLabel
                    label={rowData.statusConfig.label}
                    color={rowData.statusConfig.color}
                />
            )
        },
        sortable: true,
        width: 110,
        filterType: LUX_INPUT_TYPE.select,
        appendTo: 'self' as 'self', // capire se cè un modo migliore
    },
    {
        field: '',
        template: LUX_TEMPLATE_TYPE.actions,
    },
]

export interface ActionExecutionProps {
    actions: ActionsProps
    storeId: string
    loading: boolean
    isMultiStore: boolean
    storeList: any[]
    taskEmptyAnswer: string
    taskEmptyAnswerAttachments: { attachmentList: { value: any[] } }
    assignment: string[]
    emptyAnswerStatus: string
    hasNoAction: boolean
    taskCreationDate: any
    isPhotographicTask: boolean
    numberOfPhotos: number
    projectTitle: string
    projectTypeLabel: string
    projectType?: string
    canUpload: boolean
    user: any
    projectId: string
    showReassignApprover?: boolean
    showAcceptDisclaimer?: boolean
    onSelectedStore: (storeId: string) => void
    onTaskEmptyAnswer: (value) => void
    onTaskEmptyAnswerSave: () => void
    onTaskEmptyAnswerAttachments: (value) => void
    onReassignApprover: () => void
}

const Action = (props: ActionExecutionProps) => {
    const {
        actions,
        storeId,
        storeList,
        taskEmptyAnswer,
        taskEmptyAnswerAttachments,
        assignment,
        emptyAnswerStatus,
        hasNoAction,
        taskCreationDate,
        isPhotographicTask,
        numberOfPhotos,
        projectTitle,
        projectTypeLabel,
        projectType,
        canUpload,
        user,
        projectId,
        showAcceptDisclaimer,
        showReassignApprover,
        onSelectedStore,
        onTaskEmptyAnswer,
        onTaskEmptyAnswerSave,
        onTaskEmptyAnswerAttachments,
        onReassignApprover,
    } = props

    const componentClass = 'smart-modals-news-execution-action'

    const intl = useIntl()

    const [sortOrder, setSortOrder] = useState(1)
    const [localStoreList, setLocalStoreList] = useState([])
    const [filters, setFilters] = useState({
        storeName: {
            value: '',
            matchMode: FilterMatchMode.CONTAINS,
        },
        status: {
            value: undefined,
            matchMode: FilterMatchMode.STARTS_WITH,
            options: [],
        },
    })

    const initFilters = () => {
        const statusList = storeList
            ? storeList.map((store) =>
                  intl.formatMessage({
                      id: `smart.project.status.${store.status}`,
                      defaultMessage: store.status,
                  })
              )
            : []
        setFilters({
            storeName: {
                value: '',
                matchMode: FilterMatchMode.CONTAINS,
            },
            status: {
                value: undefined,
                matchMode: FilterMatchMode.STARTS_WITH,
                options: statusList.filter((item, pos) => statusList.indexOf(item) == pos),
            },
        })
    }

    useEffect(() => {
        const statusList = storeList
            ? storeList.map((store) =>
                  intl.formatMessage({
                      id: `smart.project.status.${store.status}`,
                      defaultMessage: store.status,
                  })
              )
            : []
        setFilters({
            storeName: {
                value: '',
                matchMode: FilterMatchMode.CONTAINS,
            },
            status: {
                value: undefined,
                matchMode: FilterMatchMode.STARTS_WITH,
                options: statusList.filter((item, pos) => statusList.indexOf(item) == pos),
            },
        })
    }, [])

    useEffect(() => {
        setLocalStoreList(
            storeList?.map((store) => ({
                ...store,
                disabled:
                    (projectType === LayoutActivityType.SURVEY_TASK.toString() &&
                        store.assignmentUserId?.length &&
                        !store.assignmentUserId.includes(user.user_id)) ||
                    (isPhotographicTask &&
                        !store.isEnabledExec &&
                        !store.isEnabledApprove &&
                        store.status !== SectionFilter.closed &&
                        store.status !== SectionFilter.inApproval) ||
                    (!isPhotographicTask &&
                        store.hasOwnProperty('isEnabledExec') &&
                        !store.isEnabledExec),
                status: intl.formatMessage({
                    id: `smart.project.status.${store.status}`,
                    defaultMessage: store.status,
                }),
                statusConfig: {
                    label: intl.formatMessage({
                        id: `smart.project.status.${store.status}`,
                        defaultMessage: store.status,
                    }),
                    color: statusColors[store.status],
                    key: store.status,
                },
            }))
        )
    }, [storeList])

    const onChangeFilter = (value: any, field: string) =>
        setFilters({
            ...filters,
            [field]: { ...filters[field], value: value },
        })

    const goToStoreExecution = (storeId) => {
        onSelectedStore(storeId)
    }

    const isEmptyTask = () => {
        return (
            actions?.questions &&
            !actions?.questions.count &&
            actions?.tasks &&
            !actions?.tasks.count &&
            actions?.surveys &&
            !actions?.surveys.count &&
            !hasNoAction
        )
    }

    const onUpload = (files) => {
        onTaskEmptyAnswerAttachments({
            attachmentList: {
                value: [
                    ...taskEmptyAnswerAttachments.attachmentList.value,
                    ...files.map((file: any) => ({
                        file: file.file,
                        url: file.url,
                        id: file.id,
                    })),
                ],
            },
        })
    }

    const onRemoveAttachment = (file) => {
        let newList = [...taskEmptyAnswerAttachments.attachmentList.value]
        const fileIndex = newList.findIndex((i) => i.file.name === file.name)
        newList.splice(fileIndex, 1)
        onTaskEmptyAnswerAttachments({
            attachmentList: {
                value: [...newList],
            },
        })
    }

    const onSort = (e) => {
        setSortOrder((value) => (value === 1 ? -1 : 1))
        setLocalStoreList((value) =>
            value.sort((a, b) => {
                if (a.status.label < b.status.label) {
                    return sortOrder
                }
                return -sortOrder
            })
        )
    }

    const onClickRow = (store) => {
        const isApprover = store.approvalUserInfo?.find(
            (u) => u.userId === user.user_id && u.isAccepted
        )

        if (!isApprover || projectType === SmartlyActionSubtypes.SERVICE_REQUEST) {
            goToStoreExecution(store.storeId)
            return
        }
        let url = `/gallery/detail/${projectId}?selectedStores=${store.storeId}`
        window.location.href = window.location.origin + url
    }

    return (
        <div className={componentClass}>
            {storeId && (
                <div className={`${componentClass}__actions`}>
                    {actions?.questions && actions?.questions.count > 0 && (
                        <ActionCard
                            key={1}
                            id={actions.questions.id}
                            title={actions.questions.title}
                            count={actions.questions.count}
                            onClick={actions.questions.onClick}
                            progress={actions.questions.progress}
                        />
                    )}
                    {actions?.tasks && actions?.tasks.count > 0 && (
                        <ActionCard
                            key={2}
                            id={actions.tasks.id}
                            title={actions.tasks.title}
                            count={actions.tasks.count}
                            onClick={actions.tasks.onClick}
                            progress={actions.tasks.progress}
                            taskCreationDate={taskCreationDate}
                        />
                    )}
                    {actions?.surveys && actions?.surveys.count > 0 && (
                        <ActionCard
                            key={3}
                            id={actions.surveys.id}
                            title={actions.surveys.title}
                            count={actions.surveys.count}
                            onClick={actions.surveys.onClick}
                            progress={actions.surveys.progress}
                        />
                    )}
                    {isPhotographicTask && (
                        <Photo
                            projectTitle={projectTitle}
                            numberOfPhotos={numberOfPhotos}
                            projectType={projectTypeLabel}
                            storeId={storeId}
                            canUpload={canUpload}
                        />
                    )}
                    {isEmptyTask() &&
                        emptyAnswerStatus &&
                        assignment.length > 0 &&
                        !isPhotographicTask && (
                            <>
                                {canExecuteQuestionTask({
                                    status: emptyAnswerStatus,
                                    module: SmartlyActionSubtypes.COMMUNICATION,
                                    type: SmartlyActionSubtypes.COMMUNICATION,
                                    assignment: assignment,
                                }) && (
                                    <LuxRichInputText
                                        value={taskEmptyAnswer}
                                        onChangeCallback={onTaskEmptyAnswer}
                                        placeholder={intl.formatMessage({
                                            id: 'modals.new.activity.section.main.info.rich.text.input.placeholder',
                                            defaultMessage: 'Type here',
                                        })}
                                        label={intl.formatMessage({
                                            id: 'modals.activity.execution.empty.answer',
                                            defaultMessage: 'Answer to the Store',
                                        })}
                                        fileUploadUrl={ProjectsService.FROALA_UPLOAD_URL}
                                        config={{
                                            events: {
                                                initialized() {
                                                    const editor = this
                                                    editor.edit.on()
                                                },
                                            },
                                        }}
                                    />
                                )}
                                {!canExecuteQuestionTask({
                                    status: emptyAnswerStatus,
                                    module: 'communication',
                                    type: 'communication',
                                    assignment: assignment,
                                }) && (
                                    <LuxRichInputText
                                        value={taskEmptyAnswer}
                                        onChangeCallback={onTaskEmptyAnswer}
                                        placeholder={intl.formatMessage({
                                            id: 'modals.new.activity.section.main.info.rich.text.input.placeholder',
                                            defaultMessage: 'Type here',
                                        })}
                                        label={intl.formatMessage({
                                            id: 'modals.activity.execution.empty.answer',
                                            defaultMessage: 'Answer to the Store',
                                        })}
                                        fileUploadUrl={ProjectsService.FROALA_UPLOAD_URL}
                                        config={{
                                            events: {
                                                initialized() {
                                                    const editor = this
                                                    editor.edit.off()
                                                },
                                            },
                                        }}
                                    />
                                )}
                                {canExecuteQuestionTask({
                                    status: emptyAnswerStatus,
                                    module: 'communication',
                                    type: 'communication',
                                    assignment: assignment,
                                }) && (
                                    <SectionItem>
                                        <AddAttachment
                                            id={`empty-answer-${Math.random() * 100}`}
                                            form={taskEmptyAnswerAttachments}
                                            onUpload={(form) => onUpload(form)}
                                            showRemoveIcon
                                            showSplitFile={false}
                                            onRemove={(file) => onRemoveAttachment(file)}
                                        />
                                    </SectionItem>
                                )}
                                {canExecuteQuestionTask({
                                    status: emptyAnswerStatus,
                                    module: 'communication',
                                    type: 'communication',
                                    assignment: assignment,
                                }) && (
                                    <div className={`${componentClass}__empty-answer-cta`}>
                                        <LuxButton
                                            onClick={onTaskEmptyAnswerSave}
                                            text={intl.formatMessage({
                                                id: 'modals.new.activity.section.save',
                                                defaultMessage: 'Save',
                                            })}
                                            disabled={
                                                !taskEmptyAnswer &&
                                                taskEmptyAnswerAttachments.attachmentList.value
                                                    .length > 0
                                            }
                                            variant="outlined"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                </div>
            )}
            {!storeId && (
                <>
                    <ActivitySummaryChart />
                    <LuxDataTable
                        label={intl.formatMessage({
                            id: 'modals.new.activity.section.distribution.store.list',
                            defaultMessage: 'Store List',
                        })}
                        columns={columns.map((column) => ({
                            ...column,
                            header: column.header
                                ? intl.formatMessage({
                                      id: column.header,
                                      defaultMessage: column.header.split('.').pop(),
                                  })
                                : '',
                        }))}
                        dataSet={localStoreList}
                        scrollable
                        totalRecords={storeList?.length}
                        actionsList={[
                            {
                                icon: 'caret-right',
                                command: (e) => onClickRow(e),
                            },
                        ]}
                        columnFilters={filters}
                        onFilterChange={onChangeFilter}
                        onSort={onSort}
                        sortOrder={sortOrder}
                        sortField="status"
                        onCloseFilters={initFilters}
                        labels={{
                            filter: intl.formatMessage({
                                id: 'modals.new_resource.distribution.store_list.filter',
                                defaultMessage: 'filter',
                            }),
                            rearrange: intl.formatMessage({
                                id: 'modals.new_resource.distribution.store_list.rearrange',
                                defaultMessage: 'rearrange',
                            }),
                            of: intl.formatMessage({
                                id: 'modals.new_resource.distribution.store_list.labels.of',
                                defaultMessage: 'of',
                            }),
                            selected: intl.formatMessage({
                                id: 'modals.new_resource.distribution.store_list.labels.selected',
                                defaultMessage: 'selected',
                            }),
                            emptyMessage: intl.formatMessage({
                                id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                                defaultMessage: 'No results to show.',
                            }),
                        }}
                    />
                </>
            )}
            <div className={`${componentClass}__cta-container`}>
                {showReassignApprover && (
                    <LuxButton
                        variant={'outlined'}
                        text={intl.formatMessage({
                            id: 'ph2.modals.news_execution.button.reassign_approver',
                            defaultMessage: 'Reassign approver',
                        })}
                        onClick={onReassignApprover}
                    />
                )}
            </div>
            {showAcceptDisclaimer && projectType == SmartlyActionSubtypes.SERVICE_REQUEST && (
                <div className="flex my-3 mx-2 align-items-center">
                    <LuxIcon icon="info" classes="mr-1" size={14} />
                    <div className="text-mini">
                        {intl.formatMessage({
                            id: 'modals.activity.execution.accept.disclaimer',
                            defaultMessage: 'To execute a task you must first accept it',
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    user: state.userReducer.user,
})

const mapDispatchToProps = (dispatch: any) => ({
    setIsMultiStore: (value) => {
        dispatch(setIsMultiStore(value))
    },
})

export default connect(mapStateToProps, mapDispatchToProps)(Action)
